import React, { useState, useEffect, useMemo } from 'react'
import {
  getLibrarySections,
  getLibraryBooks,
  reserveLibraryBook,
  approveLibraryReservation,
  rejectLibraryReservation,
  returnLibraryBook,
  getUserReservations,
  getAllReservations,
  createLibrarySection,
  addLibraryBook,
  deleteLibraryBook,
  loginLibraryUser,
  createLibraryMember,
  getLibraryMembers,
  ADMIN_EMAIL,
  ADMIN_PASSWORD,
  isUsingLocalFallback
} from '../services/library'

import {
  BookOpen,
  Search,
  Lock,
  Plus,
  Trash2,
  Check,
  Clock,
  Book,
  FileText,
  Globe,
  Sparkles,
  PlusCircle,
  FolderPlus,
  LayoutDashboard,
  LogOut,
  Calendar,
  AlertCircle,
  X,
  Library as LibraryIcon,
  ChevronRight,
  GraduationCap,
  ThumbsUp,
  UserPlus,
  Users
} from 'lucide-react'
import './Library.css'

// Preset cover gradients for premium visual style
const COVER_GRADIENTS = [
  { name: 'Azul Profundo', value: 'linear-gradient(135deg, #1A2B5B 0%, #2a4a8a 100%)' },
  { name: 'Naranja Fuego', value: 'linear-gradient(135deg, #FF6B35 0%, #cc2b5e 100%)' },
  { name: 'Verde Esmeralda', value: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' },
  { name: 'Morado Real', value: 'linear-gradient(135deg, #6441A5 0%, #2a0845 100%)' },
  { name: 'Oscuro Elegante', value: 'linear-gradient(135deg, #2c3e50 0%, #0f1a3a 100%)' },
  { name: 'Rosa Mágico', value: 'linear-gradient(135deg, #ec008c 0%, #fc6767 100%)' }
]

// Preset cover icons
const COVER_ICONS = [
  { id: 'math', label: 'Matemáticas', component: GraduationCap },
  { id: 'physics', label: 'Física', component: Sparkles },
  { id: 'code', label: 'Programación', component: Book },
  { id: 'globe', label: 'Investigación', component: Globe },
  { id: 'book', label: 'General/Texto', component: FileText }
]

const Library = () => {

  // Gated Library Session State
  const [libraryUser, setLibraryUser] = useState(null)

  // Local Fallback Warning State
  const [localModeActive, setLocalModeActive] = useState(false)

  useEffect(() => {
    const checkLocalMode = () => {
      setLocalModeActive(isUsingLocalFallback())
    }
    checkLocalMode()
    window.addEventListener('vtic_library_mode_change', checkLocalMode)
    return () => window.removeEventListener('vtic_library_mode_change', checkLocalMode)
  }, [])

  // Tab State: 'books' | 'my-reservations' | 'admin'

  const [activeTab, setActiveTab] = useState('books')

  // Data States
  const [sections, setSections] = useState([])
  const [books, setBooks] = useState([])
  const [userReservations, setUserReservations] = useState([])
  const [allReservations, setAllReservations] = useState([])
  const [libraryMembers, setLibraryMembers] = useState([]) // Registered students (Admin only)

  // Loading & Error States
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  // Search & Filter States
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSection, setSelectedSection] = useState('all')

  // Selected Book for Detail Modal
  const [selectedBook, setSelectedBook] = useState(null)

  // Library Gate Login States
  const [gateEmail, setGateEmail] = useState('')
  const [gatePassword, setGatePassword] = useState('')
  const [gateError, setGateError] = useState('')

  // Admin Actions Panel Tab: 'add-book' | 'add-section' | 'reservations-monitor' | 'register-student' | 'view-students'
  const [adminSubTab, setAdminSubTab] = useState('add-book')

  // Add Book Form state
  const [newBookTitle, setNewBookTitle] = useState('')
  const [newBookAuthor, setNewBookAuthor] = useState('')
  const [newBookSection, setNewBookSection] = useState('')
  const [newBookSynopsis, setNewBookSynopsis] = useState('')
  const [newBookGradient, setNewBookGradient] = useState(COVER_GRADIENTS[0].value)
  const [newBookIcon, setNewBookIcon] = useState('book')

  // Add Section Form state
  const [newSectionName, setNewSectionName] = useState('')
  const [newSectionDesc, setNewSectionDesc] = useState('')

  // Register Student Form state (Admin only)
  const [newStudentName, setNewStudentName] = useState('')
  const [newStudentEmail, setNewStudentEmail] = useState('')
  const [newStudentPassword, setNewStudentPassword] = useState('')

  // Check on mount if there is an active library session in sessionStorage
  useEffect(() => {
    const savedUser = sessionStorage.getItem('vtic_library_user')
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser)
        setLibraryUser(parsed)
        if (parsed.role === 'admin') {
          setActiveTab('admin')
        } else {
          setActiveTab('books')
        }
      } catch {
        sessionStorage.removeItem('vtic_library_user')
      }
    }
  }, [])

  // Load all base data in parallel for optimal loading times
  const loadData = async () => {
    if (!libraryUser) return
    setLoading(true)
    setError('')
    try {
      const isStudent = libraryUser.role === 'student'
      const isAdmin = libraryUser.role === 'admin'

      // Construct parallel promises array
      const promises = [
        getLibrarySections(),
        getLibraryBooks()
      ]

      if (isStudent) {
        promises.push(getUserReservations(libraryUser.id))
      } else if (isAdmin) {
        promises.push(getAllReservations())
        promises.push(getLibraryMembers())
      }

      // Execute all queries in parallel (eliminates waterfall requests)
      const results = await Promise.all(promises)

      const sectionsRes = results[0]
      const booksRes = results[1]

      if (sectionsRes.success) {
        setSections(sectionsRes.data)
        if (sectionsRes.data.length > 0 && !newBookSection) {
          setNewBookSection(sectionsRes.data[0].name)
        }
      }
      
      if (booksRes.success) {
        setBooks(booksRes.data)
      }

      if (isStudent) {
        const resRes = results[2]
        if (resRes && resRes.success) {
          setUserReservations(resRes.data)
        }
      } else if (isAdmin) {
        const allRes = results[2]
        const membersRes = results[3]

        if (allRes && allRes.success) {
          setAllReservations(allRes.data)
        }
        if (membersRes && membersRes.success) {
          setLibraryMembers(membersRes.data)
        }
      }
    } catch {
      setError('Error al conectar con la base de datos de la biblioteca.')
    } finally {
      setLoading(false)
    }
  };


  useEffect(() => {
    loadData()
  }, [libraryUser])

  // Clear Alerts
  const triggerSuccess = (msg) => {
    setSuccessMessage(msg)
    setTimeout(() => setSuccessMessage(''), 4000)
  }

  const triggerError = (msg) => {
    setError(msg)
    setTimeout(() => setError(''), 4000)
  }

  // Handle Library Gated Login Submit
  const handleGateLogin = async (e) => {
    e.preventDefault()
    setGateError('')
    setActionLoading(true)

    const result = await loginLibraryUser(gateEmail, gatePassword)
    if (result.success) {
      setGateEmail('')
      setGatePassword('')
      setLibraryUser(result.user)
      sessionStorage.setItem('vtic_library_user', JSON.stringify(result.user))
      
      triggerSuccess(`Bienvenido a la biblioteca, ${result.user.fullName}`)
      
      if (result.user.role === 'admin') {
        setActiveTab('admin')
      } else {
        setActiveTab('books')
      }
    } else {
      setGateError(result.error || 'Credenciales incorrectas.')
    }
    setActionLoading(false)
  };

  // Logout from Library (clears local gated state)
  const handleLibraryLogout = () => {
    sessionStorage.removeItem('vtic_library_user')
    setLibraryUser(null)
    setUserReservations([])
    setAllReservations([])
    setLibraryMembers([])
    setActiveTab('books')
    triggerSuccess('Sesión de biblioteca cerrada correctamente.')
  };

  // Reserve Book Action (Creates request as pending approval)
  const handleReserve = async (book) => {
    if (!libraryUser || libraryUser.role !== 'student') {
      triggerError('Acceso denegado. Solo estudiantes pueden reservar libros.')
      return
    }

    setActionLoading(true)
    try {
      const res = await reserveLibraryBook(
        book.id,
        book.title,
        book.author,
        libraryUser.id,
        libraryUser.fullName,
        libraryUser.email
      )

      if (res.success) {
        triggerSuccess('¡Solicitud enviada! Tu reserva está pendiente de aprobación por el Administrador.')
        setSelectedBook(null)
        loadData() // Refresh books and user reservations
      } else {
        triggerError(res.error || 'No se pudo solicitar la reserva.')
      }
    } catch {
      triggerError('Ocurrió un error inesperado al solicitar la reserva.')
    } finally {
      setActionLoading(false)
    }
  };

  // Approve Reservation Action (Admin)
  const handleApprove = async (reservationId, bookId) => {
    setActionLoading(true)
    try {
      const res = await approveLibraryReservation(reservationId, bookId)
      if (res.success) {
        triggerSuccess(`Reserva aprobada. Vence el ${new Date(res.dueDate).toLocaleDateString()}`)
        loadData()
      } else {
        triggerError(res.error || 'Error al aprobar la reserva.')
      }
    } catch {
      triggerError('Ocurrió un error al aprobar la reserva.')
    } finally {
      setActionLoading(false)
    }
  };

  // Reject Reservation Action (Admin)
  const handleReject = async (reservationId, bookId) => {
    setActionLoading(true)
    try {
      const res = await rejectLibraryReservation(reservationId, bookId)
      if (res.success) {
        triggerSuccess('Solicitud de reserva rechazada. El libro vuelve a estar disponible.')
        loadData()
      } else {
        triggerError(res.error || 'Error al rechazar la reserva.')
      }
    } catch {
      triggerError('Ocurrió un error al rechazar la reserva.')
    } finally {
      setActionLoading(false)
    }
  };

  // Return Book Action
  const handleReturn = async (reservation) => {
    setActionLoading(true)
    try {
      const res = await returnLibraryBook(reservation.id, reservation.bookId)
      if (res.success) {
        triggerSuccess('Libro devuelto con éxito. ¡Gracias por mantener la biblioteca disponible!')
        loadData()
      } else {
        triggerError(res.error || 'No se pudo procesar la devolución.')
      }
    } catch {
      triggerError('Error al procesar la devolución.')
    } finally {
      setActionLoading(false)
    }
  };

  // Create Section Action
  const handleCreateSection = async (e) => {
    e.preventDefault()
    if (!newSectionName.trim()) return

    setActionLoading(true)
    try {
      const res = await createLibrarySection(newSectionName.trim(), newSectionDesc.trim())
      if (res.success) {
        triggerSuccess(`Sección "${newSectionName}" creada correctamente.`)
        
        // Auto-select this section if it is the first one
        if (sections.length === 0) {
          setNewBookSection(newSectionName.trim())
        }
        
        setNewSectionName('')
        setNewSectionDesc('')
        loadData()
      } else {
        triggerError(res.error || 'Error al crear la sección.')
      }
    } catch {
      triggerError('Error de red al crear la sección.')
    } finally {
      setActionLoading(false)
    }
  };

  // Add Book Action
  const handleAddBook = async (e) => {
    e.preventDefault()
    if (!newBookTitle.trim() || !newBookAuthor.trim() || !newBookSection) {
      triggerError('Por favor completa los campos requeridos (Título, Autor y Sección)')
      return
    }

    setActionLoading(true)
    try {
      const bookData = {
        title: newBookTitle.trim(),
        author: newBookAuthor.trim(),
        section: newBookSection,
        synopsis: newBookSynopsis.trim() || 'Sin sinopsis disponible.',
        coverColor: newBookGradient,
        coverIcon: newBookIcon
      }

      const res = await addLibraryBook(bookData)
      if (res.success) {
        triggerSuccess(`Libro "${newBookTitle}" añadido al catálogo.`)
        setNewBookTitle('')
        setNewBookAuthor('')
        setNewBookSynopsis('')
        loadData()
      } else {
        triggerError(res.error || 'Error al añadir el libro.')
      }
    } catch {
      triggerError('Error al guardar el libro.')
    } finally {
      setActionLoading(false)
    }
  };

  // Delete Book Action
  const handleDeleteBook = async (bookId, title) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar el libro "${title}" permanentemente de la biblioteca?`)) {
      return
    }

    setActionLoading(true)
    try {
      const res = await deleteLibraryBook(bookId)
      if (res.success) {
        triggerSuccess('Libro eliminado permanentemente.')
        loadData()
      } else {
        triggerError(res.error || 'No se pudo eliminar el libro.')
      }
    } catch {
      triggerError('Error al eliminar el libro.')
    } finally {
      setActionLoading(false)
    }
  };

  // Create Student Member Action (Admin Only)
  const handleRegisterStudent = async (e) => {
    e.preventDefault()
    if (!newStudentName.trim() || !newStudentEmail.trim() || !newStudentPassword.trim()) {
      triggerError('Completa todos los campos para registrar al estudiante.')
      return
    }

    setActionLoading(true)
    try {
      const res = await createLibraryMember(
        newStudentName.trim(),
        newStudentEmail.trim(),
        newStudentPassword.trim()
      )

      if (res.success) {
        triggerSuccess(`Estudiante "${newStudentName}" registrado con éxito.`)
        setNewStudentName('')
        setNewStudentEmail('')
        setNewStudentPassword('')
        loadData() // Refresh student list
      } else {
        triggerError(res.error || 'Error al registrar al estudiante.')
      }
    } catch {
      triggerError('Ocurrió un error al registrar al estudiante.')
    } finally {
      setActionLoading(false)
    }
  };

  // Filtered Books Memo
  const filteredBooks = useMemo(() => {
    return books.filter(book => {
      const matchesSearch =
        book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        book.author.toLowerCase().includes(searchQuery.toLowerCase())
      
      const matchesSection = selectedSection === 'all' || book.section === selectedSection

      return matchesSearch && matchesSection
    })
  }, [books, searchQuery, selectedSection])

  // Split reservations for admin
  const adminPendingReservations = useMemo(() => {
    return allReservations.filter(r => r.status === 'pending_approval')
  }, [allReservations])

  const adminHistoricalReservations = useMemo(() => {
    return allReservations.filter(r => r.status !== 'pending_approval')
  }, [allReservations])

  // Get matching Lucide Icon for book cover
  const getCoverIconComponent = (iconName) => {
    const matched = COVER_ICONS.find(item => item.id === iconName)
    const IconComponent = matched ? matched.component : FileText
    return <IconComponent size={32} className="cover-lucide-icon" />
  };

  // ==========================================
  // RENDER GATE LOGIN (IF NOT AUTHENTICATED)
  // ==========================================
  if (!libraryUser) {
    return (
      <div className="library-page fade-in">
        {localModeActive && (
          <div className="library-local-banner">
            <AlertCircle size={18} className="banner-icon" />
            <div className="banner-text">
              <strong>Modo Local Activado:</strong> Las reglas de Firestore en la nube no permiten acceder a la biblioteca. La base de datos se ha guardado localmente en tu navegador para que puedas probar el flujo completo con total normalidad.
            </div>
            <button className="banner-reset-btn" onClick={() => {
              if (window.confirm("¿Deseas restablecer los datos locales y reintentar la conexión con Firestore?")) {
                localStorage.removeItem('vtic_library_use_local')
                localStorage.removeItem('vtic_local_library_members')
                localStorage.removeItem('vtic_local_library_sections')
                localStorage.removeItem('vtic_local_library_books')
                localStorage.removeItem('vtic_local_library_reservations')
                window.location.reload()
              }
            }}>
              Reintentar Nube
            </button>
          </div>
        )}
        <section className="library-hero gate-hero">

          <div className="hero-overlay"></div>
          <div className="container library-hero-container">
            <div className="hero-content">
              <div className="library-badge">
                <Sparkles size={16} />
                <span>Acceso Restringido</span>
              </div>
              <h1>Biblioteca Virtual <span className="highlight">VTIC</span></h1>
              <p>
                Inicia sesión con tus credenciales asignadas de biblioteca para poder explorar el catálogo académico, 
                ver la disponibilidad física de los textos y gestionar tus solicitudes de préstamo.
              </p>
            </div>
          </div>
        </section>

        <div className="container library-main gated-login-wrapper">
          <div className="library-auth-box">
            <div className="auth-card gate-login-card">
              <div className="auth-header">
                <Lock size={36} className="auth-header-icon" />
                <h2>Acceso Exclusivo de Biblioteca</h2>
                <p>Ingresa tus accesos autorizados. Si no dispones de una cuenta, solicítala a un Administrador.</p>
              </div>

              <form onSubmit={handleGateLogin} className="library-auth-form">
                <div className="auth-form-group">
                  <label>Correo Electrónico</label>
                  <input 
                    type="email" 
                    required 
                    placeholder="ejemplo@unah.hn o admin@vtic.com"
                    value={gateEmail}
                    onChange={(e) => setGateEmail(e.target.value)}
                  />
                </div>

                <div className="auth-form-group">
                  <label>Contraseña</label>
                  <input 
                    type="password" 
                    required 
                    placeholder="••••••••"
                    value={gatePassword}
                    onChange={(e) => setGatePassword(e.target.value)}
                  />
                </div>

                {gateError && (
                  <div className="auth-error-alert">
                    <AlertCircle size={16} />
                    <span>{gateError}</span>
                  </div>
                )}

                <button 
                  type="submit" 
                  className="auth-submit-btn" 
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Verificando...' : 'Iniciar Sesión en Biblioteca'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ==========================================
  // RENDER LIBRARY MAESTRO (IF AUTHENTICATED)
  // ==========================================
  return (
    <div className="library-page fade-in">
      {/* Toast Notifications */}
      {successMessage && (
        <div className="library-toast success">
          <Check size={20} />
          <span>{successMessage}</span>
        </div>
      )}
      {error && (
        <div className="library-toast error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {localModeActive && (
        <div className="library-local-banner" style={{ margin: '20px auto 0', maxWidth: '1200px' }}>
          <AlertCircle size={18} className="banner-icon" />
          <div className="banner-text">
            <strong>Modo Local Activado:</strong> Las reglas de Firestore en la nube no permiten acceder a la biblioteca. La base de datos se ha guardado localmente en tu navegador para que puedas probar el flujo completo con total normalidad.
          </div>
          <button className="banner-reset-btn" onClick={() => {
            if (window.confirm("¿Deseas restablecer los datos locales y reintentar la conexión con Firestore?")) {
              localStorage.removeItem('vtic_library_use_local')
              localStorage.removeItem('vtic_local_library_members')
              localStorage.removeItem('vtic_local_library_sections')
              localStorage.removeItem('vtic_local_library_books')
              localStorage.removeItem('vtic_local_library_reservations')
              window.location.reload()
            }
          }}>
            Reintentar Nube
          </button>
        </div>
      )}

      {/* Hero Section */}

      <section className="library-hero">
        <div className="hero-overlay"></div>
        <div className="container library-hero-container">
          <div className="hero-content">
            <div className="library-badge">
              <Sparkles size={16} />
              <span>Conocimiento al Alcance</span>
            </div>
            <h1>Biblioteca Virtual <span className="highlight">VTIC</span></h1>
            <p>
              Explora y solicita en préstamo libros académicos administrados de forma 
              oficial por el voluntariado de física, matemáticas e informática.
            </p>
          </div>
          <div className="library-hero-stats">
            <div className="quick-stat-box">
              <h3>{books.length}</h3>
              <p>Libros Totales</p>
            </div>
            <div className="quick-stat-box">
              <h3>{sections.length}</h3>
              <p>Secciones</p>
            </div>
            <div className="quick-stat-box">
              <h3>{books.filter(b => b.status === 'available').length}</h3>
              <p>Disponibles</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs Navigation (Conditional based on role) */}
      <div className="library-tabs-bar">
        <div className="container tabs-container">
          {libraryUser.role === 'student' ? (
            <>
              <button 
                className={`tab-btn ${activeTab === 'books' ? 'active' : ''}`}
                onClick={() => { setActiveTab('books'); setError(''); }}
              >
                <LibraryIcon size={18} />
                <span>Catálogo de Libros</span>
              </button>
              
              <button 
                className={`tab-btn ${activeTab === 'my-reservations' ? 'active' : ''}`}
                onClick={() => { setActiveTab('my-reservations'); setError(''); }}
              >
                <Clock size={18} />
                <span>Mis Reservas</span>
                {userReservations.filter(r => r.status === 'active' || r.status === 'pending_approval').length > 0 && (
                  <span className="badge-count">
                    {userReservations.filter(r => r.status === 'active' || r.status === 'pending_approval').length}
                  </span>
                )}
              </button>
            </>
          ) : (
            <button 
              className={`tab-btn ${activeTab === 'admin' ? 'active' : ''}`}
              onClick={() => { setActiveTab('admin'); setError(''); }}
            >
              <LayoutDashboard size={18} />
              <span>Administración</span>
              <span className="badge-admin">Admin</span>
            </button>
          )}

          {/* Persistent unified library logout */}
          <button className="tab-btn logout-library-tab-btn" onClick={handleLibraryLogout}>
            <LogOut size={18} />
            <span>Cerrar Biblioteca ({libraryUser.fullName.split(' ')[0]})</span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <main className="container library-main">
        {loading ? (
          <div className="library-loading-container">
            <div className="library-spinner"></div>
            <p>Cargando biblioteca de VTIC...</p>
          </div>
        ) : (
          <div className="library-content-loaded">
            {/* TAB 1: BOOK CATALOG */}
            {activeTab === 'books' && libraryUser.role === 'student' && (
              <div className="catalog-tab-content">
                {books.length === 0 ? (
                  <div className="empty-catalog-card">
                    <BookOpen size={64} className="empty-icon" />
                    <h3>Biblioteca Virtual Vacía</h3>
                    <p>
                      La Biblioteca Virtual se encuentra en proceso de catalogación y está actualmente vacía. 
                      Vuelve pronto para explorar nuevos recursos académicos de física, matemáticas e informática.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="catalog-search-bar">
                      <div className="search-input-wrapper">
                        <Search size={20} className="search-icon" />
                        <input 
                          type="text" 
                          placeholder="Buscar por título de libro o autor..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                          <button className="clear-search" onClick={() => setSearchQuery('')}>
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="catalog-layout">
                      {/* Sidebar Filters */}
                      <aside className="catalog-sidebar">
                        <h3 className="sidebar-title">Secciones Académicas</h3>
                        <ul className="section-filters-list">
                          <li>
                            <button
                              className={`filter-link ${selectedSection === 'all' ? 'active' : ''}`}
                              onClick={() => setSelectedSection('all')}
                            >
                              <BookOpen size={16} />
                              <span>Todas las Secciones</span>
                              <span className="filter-count">{books.length}</span>
                            </button>
                          </li>
                          {sections.map(section => {
                            const count = books.filter(b => b.section === section.name).length
                            return (
                              <li key={section.id}>
                                <button
                                  className={`filter-link ${selectedSection === section.name ? 'active' : ''}`}
                                  onClick={() => setSelectedSection(section.name)}
                                >
                                  <ChevronRight size={16} />
                                  <span>{section.name}</span>
                                  <span className="filter-count">{count}</span>
                                </button>
                              </li>
                            )
                          })}
                        </ul>
                      </aside>

                      {/* Books Grid */}
                      <div className="catalog-books-section">
                        <div className="section-header-row">
                          <h2>
                            {selectedSection === 'all' ? 'Todos los Libros' : `Libros de ${selectedSection}`}
                            <span className="results-count">({filteredBooks.length} resultados)</span>
                          </h2>
                        </div>

                        {filteredBooks.length === 0 ? (
                          <div className="empty-catalog-card">
                            <BookOpen size={64} className="empty-icon" />
                            <h3>No se encontraron libros</h3>
                            <p>No hay textos que coincidan con tu búsqueda en esta categoría.</p>
                          </div>
                        ) : (
                          <div className="books-grid">
                            {filteredBooks.map(book => {
                              return (
                                <div 
                                  key={book.id} 
                                  className={`book-card ${book.status !== 'available' ? `card-${book.status}` : ''}`}
                                  onClick={() => setSelectedBook(book)}
                                >
                                  {/* Custom Dynamic 3D Book Cover */}
                                  <div className="book-cover-container">
                                    <div className="book-cover-3d" style={{ background: book.coverColor || '#1A2B5B' }}>
                                      <div className="book-spine-effect"></div>
                                      <div className="book-cover-content">
                                        <div className="cover-icon-circle">
                                          {getCoverIconComponent(book.coverIcon)}
                                        </div>
                                        <h4 className="cover-title-text">{book.title}</h4>
                                        <p className="cover-author-text">{book.author}</p>
                                      </div>
                                      <div className="book-cover-glow"></div>
                                    </div>
                                  </div>

                                  {/* Card Body */}
                                  <div className="book-card-body">
                                    <span className="book-card-section">{book.section}</span>
                                    <h3 className="book-card-title">{book.title}</h3>
                                    <p className="book-card-author">Por {book.author}</p>
                                    
                                    <div className="book-card-footer">
                                      <span className={`status-badge ${book.status}`}>
                                        {book.status === 'available' && (
                                          <>
                                            <span className="status-dot green"></span>
                                            Disponible
                                          </>
                                        )}
                                        {book.status === 'pending' && (
                                          <>
                                            <span className="status-dot yellow"></span>
                                            Reserva Pendiente
                                          </>
                                        )}
                                        {book.status === 'reserved' && (
                                          <>
                                            <span className="status-dot orange"></span>
                                            En Préstamo
                                          </>
                                        )}
                                      </span>
                                      <button className="book-card-action">Ver Detalles</button>
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* TAB 2: MY RESERVATIONS */}
            {activeTab === 'my-reservations' && libraryUser.role === 'student' && (
              <div className="reservations-tab-content">
                <div className="user-reservations-dashboard">
                  <div className="reservations-header">
                    <div>
                      <h2>Mis Solicitudes y Préstamos</h2>
                      <p>Monitorea el estado de aprobación y devolución de tus libros reservados.</p>
                    </div>
                    <div className="user-badge-info">
                      <span className="user-email">{libraryUser.email}</span>
                    </div>
                  </div>

                  {userReservations.length === 0 ? (
                    <div className="empty-reservations-card">
                      <BookOpen size={48} className="empty-icon" />
                      <h3>No tienes reservas registradas</h3>
                      <p>Explora el catálogo y solicita tus libros. Toda reserva debe ser validada por un Administrador.</p>
                      <button className="goto-catalog-btn" onClick={() => setActiveTab('books')}>
                        Explorar Catálogo
                      </button>
                    </div>
                  ) : (
                    <div className="reservations-list-divided">
                      
                      {/* 1. SECCIÓN: SOLICITUDES PENDIENTES */}
                      {userReservations.filter(r => r.status === 'pending_approval').length > 0 && (
                        <div className="reservation-group-block">
                          <h3 className="res-group-title pending">Pendientes de Aprobación</h3>
                          <div className="reservations-list">
                            {userReservations.filter(r => r.status === 'pending_approval').map(res => (
                              <div key={res.id} className="reservation-item-card pending-card">
                                <div className="reservation-item-icon-wrapper pending-icon">
                                  <Clock size={24} />
                                </div>
                                <div className="reservation-item-details">
                                  <h3>{res.bookTitle}</h3>
                                  <p className="res-author">Por {res.bookAuthor}</p>
                                  <div className="res-meta-row">
                                    <span>
                                      <Calendar size={14} />
                                      Solicitado: {res.reservedAt?.seconds 
                                        ? new Date(res.reservedAt.seconds * 1000).toLocaleDateString()
                                        : new Date(res.reservedAt).toLocaleDateString()}
                                    </span>
                                    <span className="text-warning">
                                      <AlertCircle size={14} />
                                      Esperando validación del Administrador
                                    </span>
                                  </div>
                                </div>
                                <div className="reservation-item-actions">
                                  <span className="pending-status-pill">En Espera</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 2. SECCIÓN: PRÉSTAMOS ACTIVOS */}
                      {userReservations.filter(r => r.status === 'active').length > 0 && (
                        <div className="reservation-group-block">
                          <h3 className="res-group-title active">Préstamos Activos</h3>
                          <div className="reservations-list">
                            {userReservations.filter(r => r.status === 'active').map(res => {
                              const dateObj = res.dueDate?.seconds 
                                ? new Date(res.dueDate.seconds * 1000) 
                                : new Date(res.dueDate)
                              
                              const today = new Date()
                              const diffTime = dateObj - today
                              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
                              const isOverdue = diffDays < 0

                              return (
                                <div key={res.id} className={`reservation-item-card ${isOverdue ? 'overdue' : 'active-card'}`}>
                                  <div className="reservation-item-icon-wrapper active-icon">
                                    <BookOpen size={24} />
                                  </div>
                                  <div className="reservation-item-details">
                                    <h3>{res.bookTitle}</h3>
                                    <p className="res-author">Por {res.bookAuthor}</p>
                                    <div className="res-meta-row">
                                      <span>
                                        <Calendar size={14} />
                                        Aprobado: {res.approvedAt?.seconds 
                                          ? new Date(res.approvedAt.seconds * 1000).toLocaleDateString()
                                          : new Date(res.approvedAt).toLocaleDateString()}
                                      </span>
                                      <span className={isOverdue ? 'text-overdue' : 'text-due'}>
                                        <Clock size={14} />
                                        {isOverdue 
                                          ? `Vencido hace ${Math.abs(diffDays)} días` 
                                          : `Vence en ${diffDays} días (${dateObj.toLocaleDateString()})`}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="reservation-item-actions">
                                    <button 
                                      className="return-book-btn" 
                                      onClick={() => handleReturn(res)}
                                      disabled={actionLoading}
                                    >
                                      Devolver Libro
                                    </button>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}

                      {/* 3. SECCIÓN: HISTORIAL DE DEVOLUCIONES Y RECHAZOS */}
                      {userReservations.filter(r => r.status !== 'pending_approval' && r.status !== 'active').length > 0 && (
                        <div className="returned-history-section">
                          <h3>Historial de Solicitudes Anteriores</h3>
                          <div className="history-table-wrapper">
                            <table className="history-table">
                              <thead>
                                <tr>
                                  <th>Libro</th>
                                  <th>Fecha Solicitud</th>
                                  <th>Estado</th>
                                </tr>
                              </thead>
                              <tbody>
                                {userReservations.filter(r => r.status !== 'pending_approval' && r.status !== 'active').map(res => (
                                  <tr key={res.id}>
                                    <td>
                                      <div className="history-book-cell">
                                        <strong>{res.bookTitle}</strong>
                                        <span>{res.bookAuthor}</span>
                                      </div>
                                    </td>
                                    <td>
                                      {res.reservedAt?.seconds 
                                        ? new Date(res.reservedAt.seconds * 1000).toLocaleDateString()
                                        : new Date(res.reservedAt).toLocaleDateString()}
                                    </td>
                                    <td>
                                      <span className={`history-returned-badge ${res.status}`}>
                                        {res.status === 'returned' && 'Devuelto'}
                                        {res.status === 'rejected' && 'Rechazada'}
                                        {res.status === 'cancelled' && 'Cancelada'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TAB 3: ADMINISTRATOR PANEL */}
            {activeTab === 'admin' && libraryUser.role === 'admin' && (
              <div className="admin-tab-content">
                <div className="admin-dashboard-layout">
                  <div className="admin-header-row">
                    <div>
                      <h2>Panel de Control Bibliotecario</h2>
                      <p>Añade libros, configura secciones, registra estudiantes y valida préstamos.</p>
                    </div>
                  </div>

                  {/* Admin Dashboard Stats */}
                  <div className="admin-stats-row">
                    <div className="admin-stat-card">
                      <BookOpen size={24} />
                      <div>
                        <h4>{books.length}</h4>
                        <p>Libros Registrados</p>
                      </div>
                    </div>
                    <div className="admin-stat-card">
                      <Clock size={24} />
                      <div>
                        <h4>{allReservations.filter(r => r.status === 'pending_approval').length}</h4>
                        <p>Por Aprobar</p>
                      </div>
                    </div>
                    <div className="admin-stat-card">
                      <Check size={24} />
                      <div>
                        <h4>{books.filter(b => b.status === 'reserved').length}</h4>
                        <p>En Préstamo</p>
                      </div>
                    </div>
                    <div className="admin-stat-card">
                      <Users size={24} />
                      <div>
                        <h4>{libraryMembers.length}</h4>
                        <p>Estudiantes de la Biblioteca</p>
                      </div>
                    </div>
                  </div>

                  {/* Sub-tabs Panel */}
                  <div className="admin-tabs-nav">
                    <button 
                      className={`admin-subtab-btn ${adminSubTab === 'add-book' ? 'active' : ''}`}
                      onClick={() => setAdminSubTab('add-book')}
                    >
                      <PlusCircle size={16} />
                      Subir Libro
                    </button>
                    <button 
                      className={`admin-subtab-btn ${adminSubTab === 'add-section' ? 'active' : ''}`}
                      onClick={() => setAdminSubTab('add-section')}
                    >
                      <FolderPlus size={16} />
                      Crear Sección
                    </button>
                    <button 
                      className={`admin-subtab-btn ${adminSubTab === 'reservations-monitor' ? 'active' : ''}`}
                      onClick={() => setAdminSubTab('reservations-monitor')}
                    >
                      <Clock size={16} />
                      Reservas ({allReservations.filter(r => r.status === 'pending_approval' || r.status === 'active').length})
                    </button>
                    <button 
                      className={`admin-subtab-btn ${adminSubTab === 'register-student' ? 'active' : ''}`}
                      onClick={() => setAdminSubTab('register-student')}
                    >
                      <UserPlus size={16} />
                      Registrar Estudiante
                    </button>
                    <button 
                      className={`admin-subtab-btn ${adminSubTab === 'view-students' ? 'active' : ''}`}
                      onClick={() => setAdminSubTab('view-students')}
                    >
                      <Users size={16} />
                      Ver Estudiantes ({libraryMembers.length})
                    </button>
                  </div>

                  {/* Sub-tab Content Render */}
                  <div className="admin-panel-content">
                    {/* Sub-tab: ADD BOOK */}
                    {adminSubTab === 'add-book' && (
                      <div className="admin-form-container">
                        <h3>Subir Nuevo Libro Académico</h3>
                        {sections.length === 0 ? (
                          <div className="admin-warning-empty-sections">
                            <AlertCircle size={24} className="warning-icon" />
                            <div>
                              <h4>No hay secciones creadas todavía</h4>
                              <p>Antes de subir libros, debes crear al menos una sección académica para organizarlos (ej. Matemáticas, Física).</p>
                              <button className="goto-catalog-btn" onClick={() => setAdminSubTab('add-section')}>
                                Crear Sección Ahora
                              </button>
                            </div>
                          </div>
                        ) : (
                          <form onSubmit={handleAddBook} className="admin-editor-form">
                            <div className="form-double-column">
                              <div className="form-group">
                                <label>Título del Libro *</label>
                                <input 
                                  type="text" 
                                  required 
                                  placeholder="Ej. Cálculo de una variable"
                                  value={newBookTitle}
                                  onChange={(e) => setNewBookTitle(e.target.value)}
                                />
                              </div>
                              <div className="form-group">
                                <label>Autor *</label>
                                <input 
                                  type="text" 
                                  required 
                                  placeholder="Ej. Ron Larson"
                                  value={newBookAuthor}
                                  onChange={(e) => setNewBookAuthor(e.target.value)}
                                />
                              </div>
                            </div>

                            <div className="form-double-column">
                              <div className="form-group">
                                <label>Sección Académica *</label>
                                  <select 
                                    value={newBookSection} 
                                    onChange={(e) => setNewBookSection(e.target.value)}
                                  >
                                    {sections.map(sec => (
                                      <option key={sec.id} value={sec.name}>{sec.name}</option>
                                    ))}
                                  </select>
                              </div>
                              <div className="form-group">
                                <label>Icono de Portada *</label>
                                <div className="icons-selector-grid">
                                  {COVER_ICONS.map(ico => {
                                    const IcoComponent = ico.component
                                    return (
                                      <button
                                        key={ico.id}
                                        type="button"
                                        className={`icon-select-btn ${newBookIcon === ico.id ? 'active' : ''}`}
                                        onClick={() => setNewBookIcon(ico.id)}
                                        title={ico.label}
                                      >
                                        <IcoComponent size={18} />
                                        <span>{ico.label}</span>
                                      </button>
                                    )
                                  })}
                                </div>
                              </div>
                            </div>

                            {/* Portada Color Gradiente Selector */}
                            <div className="form-group">
                              <label>Diseño / Color de la Portada</label>
                              <div className="gradients-selector-list">
                                {COVER_GRADIENTS.map(grad => (
                                  <button
                                    key={grad.name}
                                    type="button"
                                    className={`gradient-select-btn ${newBookGradient === grad.value ? 'active' : ''}`}
                                    style={{ background: grad.value }}
                                    onClick={() => setNewBookGradient(grad.value)}
                                    title={grad.name}
                                  >
                                    {newBookGradient === grad.value && <Check size={16} className="checked-icon" />}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Synopsis */}
                            <div className="form-group">
                              <label>Sinopsis / Descripción del Contenido</label>
                              <textarea 
                                rows="3" 
                                placeholder="Escribe un breve resumen de los temas abordados por el libro..."
                                value={newBookSynopsis}
                                onChange={(e) => setNewBookSynopsis(e.target.value)}
                              ></textarea>
                            </div>

                            {/* Submit */}
                            <button 
                              type="submit" 
                              className="admin-submit-btn"
                              disabled={actionLoading}
                            >
                              <Plus size={18} />
                              {actionLoading ? 'Registrando...' : 'Añadir Libro al Inventario'}
                            </button>
                          </form>
                        )}

                        {/* Inventario List Table (for quick deletion) */}
                        <div className="admin-inventory-list">
                          <h3>Inventario de Libros Actuales ({books.length})</h3>
                          {books.length === 0 ? (
                            <p className="no-items-inventory">No hay libros cargados en la biblioteca.</p>
                          ) : (
                            <div className="history-table-wrapper">
                              <table className="inventory-table">
                                <thead>
                                  <tr>
                                    <th>Libro / Autor</th>
                                    <th>Sección</th>
                                    <th>Estado</th>
                                    <th>Acción</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {books.map(b => (
                                    <tr key={b.id}>
                                      <td>
                                        <strong>{b.title}</strong>
                                        <span className="block text-gray">{b.author}</span>
                                      </td>
                                      <td>{b.section}</td>
                                      <td>
                                        <span className={`status-badge-mini ${b.status}`}>
                                          {b.status === 'available' && 'Disponible'}
                                          {b.status === 'pending' && 'Reserva Pendiente'}
                                          {b.status === 'reserved' && 'Prestado'}
                                        </span>
                                      </td>
                                      <td>
                                        <button 
                                          className="delete-book-row-btn"
                                          onClick={() => handleDeleteBook(b.id, b.title)}
                                          disabled={actionLoading}
                                          title="Eliminar Libro"
                                        >
                                          <Trash2 size={16} />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Sub-tab: ADD SECTION */}
                    {adminSubTab === 'add-section' && (
                      <div className="admin-form-container">
                        <h3>Crear Nueva Sección Académica</h3>
                        <form onSubmit={handleCreateSection} className="admin-editor-form">
                          <div className="form-group">
                            <label>Nombre de la Sección *</label>
                            <input 
                              type="text" 
                              required 
                              placeholder="Ej. Química Analítica o Bioquímica"
                              value={newSectionName}
                              onChange={(e) => setNewSectionName(e.target.value)}
                            />
                          </div>

                          <div className="form-group">
                            <label>Descripción del Área Temática</label>
                            <textarea 
                              rows="3" 
                              placeholder="Describe brevemente qué tipo de asignaturas o ciencias engloba esta sección..."
                              value={newSectionDesc}
                              onChange={(e) => setNewSectionDesc(e.target.value)}
                            ></textarea>
                          </div>

                          <button 
                            type="submit" 
                            className="admin-submit-btn"
                            disabled={actionLoading}
                          >
                            <PlusCircle size={18} />
                            {actionLoading ? 'Creando...' : 'Guardar Nueva Sección'}
                          </button>
                        </form>

                        {/* List of existing sections */}
                        <div className="admin-sections-list">
                          <h3>Secciones Vigentes ({sections.length})</h3>
                          {sections.length === 0 ? (
                            <p className="no-items-inventory">No hay secciones registradas.</p>
                          ) : (
                            <div className="sections-cards-grid">
                              {sections.map(sec => (
                                <div key={sec.id} className="admin-section-preview-card">
                                  <h4>{sec.name}</h4>
                                  <p>{sec.description || 'Sin descripción detallada.'}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Sub-tab: RESERVATIONS MONITOR & APPROVAL */}
                    {adminSubTab === 'reservations-monitor' && (
                      <div className="admin-form-container">
                        
                        {/* 1. SECCIÓN CRÍTICA: SOLICITUDES PENDIENTES DE APROBACIÓN */}
                        <div className="admin-approval-requests-section">
                          <h3>Solicitudes de Reserva Pendientes de Validación ({adminPendingReservations.length})</h3>
                          <p className="section-desc-help">Verifica el libro y los datos del estudiante antes de aprobar la entrega física y activar el préstamo.</p>

                          {adminPendingReservations.length === 0 ? (
                            <div className="admin-empty-action-box">
                              <ThumbsUp size={36} className="empty-action-icon" />
                              <h4>No hay solicitudes pendientes</h4>
                              <p>Todos los préstamos solicitados por estudiantes han sido validados o resueltos.</p>
                            </div>
                          ) : (
                            <div className="history-table-wrapper admin-pending-reservations-table">
                              <table className="monitor-table">
                                <thead>
                                  <tr>
                                    <th>Libro Solicitado</th>
                                    <th>Estudiante</th>
                                    <th>Fecha Solicitud</th>
                                    <th>Acciones</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {adminPendingReservations.map(res => (
                                    <tr key={res.id} className="row-pending-alert">
                                      <td>
                                        <div className="monitor-book-cell">
                                          <strong>{res.bookTitle}</strong>
                                          <span>Por {res.bookAuthor}</span>
                                        </div>
                                      </td>
                                      <td>
                                        <div className="monitor-user-cell">
                                          <strong>{res.userName}</strong>
                                          <span>{res.userEmail}</span>
                                        </div>
                                      </td>
                                      <td>
                                        <span className="res-request-date">
                                          {res.reservedAt?.seconds 
                                            ? new Date(res.reservedAt.seconds * 1000).toLocaleDateString()
                                            : new Date(res.reservedAt).toLocaleDateString()}
                                        </span>
                                      </td>
                                      <td>
                                        <div className="admin-approval-buttons-group">
                                          <button 
                                            className="admin-btn-action-approve"
                                            onClick={() => handleApprove(res.id, res.bookId)}
                                            disabled={actionLoading}
                                            title="Aprobar Préstamo"
                                          >
                                            <Check size={14} />
                                            Aprobar
                                          </button>
                                          <button 
                                            className="admin-btn-action-reject"
                                            onClick={() => handleReject(res.id, res.bookId)}
                                            disabled={actionLoading}
                                            title="Rechazar Préstamo"
                                          >
                                            <X size={14} />
                                            Rechazar
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>

                        {/* 2. SECCIÓN: PRÉSTAMOS ACTIVOS E HISTORIAL */}
                        <div className="admin-historical-loans-section" style={{ marginTop: '50px' }}>
                          <h3>Historial de Préstamos y Entregas ({adminHistoricalReservations.length})</h3>
                          
                          {adminHistoricalReservations.length === 0 ? (
                            <p className="no-items-inventory">No hay registros históricos de préstamos en el sistema.</p>
                          ) : (
                            <div className="history-table-wrapper">
                              <table className="monitor-table">
                                <thead>
                                  <tr>
                                    <th>Libro</th>
                                    <th>Estudiante</th>
                                    <th>Fechas</th>
                                    <th>Estado</th>
                                    <th>Acción</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {adminHistoricalReservations.map(res => {
                                    const dateObj = res.dueDate?.seconds 
                                      ? new Date(res.dueDate.seconds * 1000) 
                                      : res.dueDate ? new Date(res.dueDate) : null
                                    
                                    const isActive = res.status === 'active'
                                    const isOverdue = isActive && dateObj && dateObj < new Date()

                                    return (
                                      <tr key={res.id} className={isOverdue ? 'row-overdue' : ''}>
                                        <td>
                                          <div className="monitor-book-cell">
                                            <strong>{res.bookTitle}</strong>
                                            <span>Por {res.bookAuthor}</span>
                                          </div>
                                        </td>
                                        <td>
                                          <div className="monitor-user-cell">
                                            <strong>{res.userName}</strong>
                                            <span>{res.userEmail}</span>
                                          </div>
                                        </td>
                                        <td>
                                          <div className="monitor-dates-cell">
                                            <span>Solicitud: {res.reservedAt?.seconds 
                                              ? new Date(res.reservedAt.seconds * 1000).toLocaleDateString()
                                              : new Date(res.reservedAt).toLocaleDateString()}</span>
                                            {dateObj && (
                                              <span className={isOverdue ? 'text-danger' : ''}>
                                                Límite: {dateObj.toLocaleDateString()}
                                              </span>
                                            )}
                                          </div>
                                        </td>
                                        <td>
                                          <span className={`status-badge-res ${res.status} ${isOverdue ? 'vencida' : ''}`}>
                                            {res.status === 'returned' && 'Devuelto'}
                                            {res.status === 'active' && !isOverdue && 'Préstamo Activo'}
                                            {res.status === 'active' && isOverdue && 'Vencido'}
                                            {res.status === 'rejected' && 'Rechazado'}
                                            {res.status === 'cancelled' && 'Cancelado'}
                                          </span>
                                        </td>
                                        <td>
                                          {isActive ? (
                                            <button 
                                              className="admin-release-btn"
                                              onClick={() => handleReturn(res)}
                                              disabled={actionLoading}
                                              title="Marcar como Devuelto"
                                            >
                                              <Check size={14} />
                                              Devuelto
                                            </button>
                                          ) : (
                                            <span className="monitor-completed-text">Finalizada</span>
                                          )}
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Sub-tab: REGISTER STUDENT */}
                    {adminSubTab === 'register-student' && (
                      <div className="admin-form-container">
                        <h3>Registrar Nuevo Estudiante de la Biblioteca</h3>
                        <p className="section-desc-help">Crea los accesos exclusivos de la biblioteca para un estudiante. Los estudiantes no pueden auto-registrarse.</p>
                        
                        <form onSubmit={handleRegisterStudent} className="admin-editor-form">
                          <div className="form-group">
                            <label>Nombre Completo del Estudiante *</label>
                            <input 
                              type="text" 
                              required 
                              placeholder="Ej. Carlos Mendoza"
                              value={newStudentName}
                              onChange={(e) => setNewStudentName(e.target.value)}
                            />
                          </div>

                          <div className="form-double-column">
                            <div className="form-group">
                              <label>Correo Electrónico Autorizado *</label>
                              <input 
                                type="email" 
                                required 
                                placeholder="ej. carlos@unah.hn"
                                value={newStudentEmail}
                                onChange={(e) => setNewStudentEmail(e.target.value)}
                              />
                            </div>
                            <div className="form-group">
                              <label>Contraseña Asignada *</label>
                              <input 
                                type="text" 
                                required 
                                placeholder="Ej. carlos123 (mínimo 6 caracteres)"
                                value={newStudentPassword}
                                onChange={(e) => setNewStudentPassword(e.target.value)}
                              />
                            </div>
                          </div>

                          <button 
                            type="submit" 
                            className="admin-submit-btn"
                            disabled={actionLoading}
                          >
                            <UserPlus size={18} />
                            {actionLoading ? 'Registrando...' : 'Registrar Estudiante'}
                          </button>
                        </form>
                      </div>
                    )}

                    {/* Sub-tab: VIEW STUDENTS LIST */}
                    {adminSubTab === 'view-students' && (
                      <div className="admin-form-container">
                        <h3>Estudiantes Registrados en la Biblioteca ({libraryMembers.length})</h3>
                        <p className="section-desc-help">A continuación se listan las cuentas de prueba creadas. Úsalas para evaluar e iniciar sesión en el Gate principal de la biblioteca.</p>

                        {libraryMembers.length === 0 ? (
                          <div className="admin-empty-action-box">
                            <Users size={36} className="empty-action-icon" style={{ color: 'var(--accent-orange)' }} />
                            <h4>No hay estudiantes registrados</h4>
                            <p>Usa la pestaña "Registrar Estudiante" para crear el primer acceso de estudiante.</p>
                          </div>
                        ) : (
                          <div className="history-table-wrapper">
                            <table className="monitor-table">
                              <thead>
                                <tr>
                                  <th>Nombre Estudiante</th>
                                  <th>Correo Electrónico</th>
                                  <th>Contraseña de Prueba</th>
                                  <th>Fecha Registro</th>
                                </tr>
                              </thead>
                              <tbody>
                                {libraryMembers.map(member => (
                                  <tr key={member.id}>
                                    <td>
                                      <strong>{member.fullName}</strong>
                                    </td>
                                    <td>
                                      <code>{member.email}</code>
                                    </td>
                                    <td>
                                      <code className="text-warning font-bold">{member.password}</code>
                                    </td>
                                    <td>
                                      <span className="res-request-date">
                                        {member.createdAt?.seconds 
                                          ? new Date(member.createdAt.seconds * 1000).toLocaleDateString()
                                          : member.createdAt ? new Date(member.createdAt).toLocaleDateString() : 'N/A'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* DETAIL MODAL FOR SELECTED BOOK */}
      {selectedBook && (
        <div className="library-modal-overlay" onClick={() => setSelectedBook(null)}>
          <div className="library-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setSelectedBook(null)}>
              <X size={20} />
            </button>

            <div className="modal-content-grid">
              {/* Modal Cover Area */}
              <div className="modal-cover-column" style={{ background: selectedBook.coverColor || '#1A2B5B' }}>
                <div className="modal-cover-spine"></div>
                <div className="modal-cover-main">
                  <div className="modal-icon-wrapper">
                    {getCoverIconComponent(selectedBook.coverIcon)}
                  </div>
                  <h3>{selectedBook.title}</h3>
                  <p>{selectedBook.author}</p>
                </div>
              </div>

              {/* Modal Details Column */}
              <div className="modal-details-column">
                <span className="modal-section-badge">{selectedBook.section}</span>
                <h2>{selectedBook.title}</h2>
                <p className="modal-author-name">Autor: <strong>{selectedBook.author}</strong></p>
                
                <div className="modal-synopsis-box">
                  <h4>Sinopsis Académica</h4>
                  <p>{selectedBook.synopsis}</p>
                </div>

                <div className="modal-availability-row">
                  <span className="status-label">Disponibilidad en Biblioteca:</span>
                  <span className={`status-badge large ${selectedBook.status}`}>
                    {selectedBook.status === 'available' && 'Disponible para Reserva'}
                    {selectedBook.status === 'pending' && 'Reserva Pendiente de Aprobación'}
                    {selectedBook.status === 'reserved' && 'Reservado / En Préstamo'}
                  </span>
                </div>

                <div className="modal-actions-area">
                  {selectedBook.status === 'available' ? (
                    libraryUser.role === 'student' ? (
                      <button 
                        className="modal-reserve-btn"
                        onClick={() => handleReserve(selectedBook)}
                        disabled={actionLoading}
                      >
                        <Check size={18} />
                        {actionLoading ? 'Procesando...' : 'Solicitar Reserva Ahora'}
                      </button>
                    ) : (
                      <div className="reserved-warning-card">
                        <Lock size={16} />
                        <span>Rol Administrador. Inicia sesión como estudiante para reservar.</span>
                      </div>
                    )
                  ) : (
                    <div className="reserved-warning-card">
                      <Clock size={16} />
                      <span>
                        {selectedBook.status === 'pending' 
                          ? 'Este libro ha sido solicitado y está pendiente de aprobación por el Administrador.' 
                          : 'Este ejemplar se encuentra prestado actualmente.'}
                      </span>
                    </div>
                  )}
                  <button className="modal-cancel-btn" onClick={() => setSelectedBook(null)}>
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Library
